import { useEffect, useMemo, useState } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
  FiSearch,
  FiEye,
  FiEyeOff,
  FiLayers,
  FiAlertCircle,
  FiLock,
} from "react-icons/fi";

import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { hasPermission, isSuperadmin } from "../utils/permissions";
import {
  listMasterRecords,
  createMasterRecord,
  updateMasterRecord,
  deleteMasterRecord,
} from "../../services/master_service";

import "./master_data.css";

const MASTER_TABLE_CONFIG = [
  {
    key: "assignment_type",
    path: "assignment-types",
    label: "Assignment Types",
    hasIsActive: true,
    fields: [
      { name: "assignment_type_name", label: "Assignment Type Name", type: "text", required: true },
    ],
  },
  {
    key: "service_location",
    path: "service-locations",
    label: "Service Locations",
    hasIsActive: true,
    fields: [
      { name: "location_name", label: "Location Name", type: "text", required: true },
      { name: "location_code", label: "Location Code", type: "text" },
    ],
  },
  {
    key: "branch",
    path: "branches",
    label: "Branches",
    hasIsActive: true,
    fields: [
      { name: "branch_name", label: "Branch Name", type: "text", required: true },
      { name: "location_id", label: "Service Location", type: "reference", refKey: "service_location", required: true },
    ],
  },
  {
    key: "company",
    path: "companies",
    label: "Companies",
    hasIsActive: true,
    fields: [{ name: "company_name", label: "Company Name", type: "text", required: true }],
  },
  {
    key: "financial_year",
    path: "financial-years",
    label: "Financial Years",
    hasIsActive: true,
    fields: [
      { name: "fy_label", label: "FY Label", type: "text", required: true, placeholder: "e.g. 2026-27" },
      { name: "start_date", label: "Start Date", type: "date", required: true },
      { name: "end_date", label: "End Date", type: "date", required: true },
    ],
  },
  {
    key: "fuel_issue",
    path: "fuel-issues",
    label: "Fuel Issues",
    hasIsActive: false,
    fields: [{ name: "fuel_issue_name", label: "Fuel Issue Name", type: "text", required: true }],
  },
  {
    key: "fuel_type",
    path: "fuel-types",
    label: "Fuel Types",
    hasIsActive: true,
    fields: [{ name: "fuel_type_name", label: "Fuel Type Name", type: "text", required: true }],
  },
  {
    key: "role",
    path: "roles",
    label: "Roles",
    hasIsActive: true,
    fields: [{ name: "role_name", label: "Role Name", type: "text", required: true }],
  },
  {
    key: "status",
    path: "statuses",
    label: "Statuses",
    hasIsActive: true,
    fields: [{ name: "status_name", label: "Status Name", type: "text", required: true }],
  },
  {
    key: "supplier_type",
    path: "supplier-types",
    label: "Supplier Types",
    hasIsActive: false,
    fields: [{ name: "supplier_type_name", label: "Supplier Type Name", type: "text", required: true }],
  },
  {
    key: "tax_status",
    path: "tax-statuses",
    label: "Tax Statuses",
    hasIsActive: true,
    fields: [{ name: "tax_status_name", label: "Tax Status Name", type: "text", required: true }],
  },
  {
    key: "transmission_type",
    path: "transmission-types",
    label: "Transmission Types",
    hasIsActive: true,
    fields: [{ name: "transmission_name", label: "Transmission Name", type: "text", required: true }],
  },
  {
    key: "vehicle_make",
    path: "vehicle-makes",
    label: "Vehicle Makes",
    hasIsActive: true,
    fields: [{ name: "make_name", label: "Make Name", type: "text", required: true }],
  },
  {
    key: "vehicle_model",
    path: "vehicle-models",
    label: "Vehicle Models",
    hasIsActive: true,
    fields: [
      { name: "model_name", label: "Model Name", type: "text", required: true },
      { name: "vehicle_make_id", label: "Vehicle Make", type: "reference", refKey: "vehicle_make" },
    ],
  },
];

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function findConfig(key) {
  return MASTER_TABLE_CONFIG.find((c) => c.key === key) || MASTER_TABLE_CONFIG[0];
}

function primaryFieldName(cfg) {
  return cfg.fields[0]?.name;
}

function emptyFormValues(cfg) {
  const values = {};
  cfg.fields.forEach((f) => {
    values[f.name] = "";
  });
  if (cfg.hasIsActive) values.is_active = true;
  return values;
}

export default function MasterData() {
  const superadmin = isSuperadmin();
  const canView = superadmin || hasPermission("master_data", "view");
  const canEdit = superadmin || hasPermission("master_data", "edit");
  const canDelete = superadmin || hasPermission("master_data", "delete");
  const canManage = canEdit || canDelete;

  const [activeKey, setActiveKey] = useState(MASTER_TABLE_CONFIG[0].key);
  const activeCfg = useMemo(() => findConfig(activeKey), [activeKey]);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  const [refCache, setRefCache] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  async function loadRefTable(key) {
    if (refCache[key] || !canView) return;
    try {
      const cfg = findConfig(key);
      const data = await listMasterRecords(cfg.path, true);
      setRefCache((prev) => ({ ...prev, [key]: data }));
    } catch {
    }
  }

  async function loadRecords() {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listMasterRecords(activeCfg.path, includeInactive);
      setRecords(data || []);
    } catch (err) {
      setError(err.message || `Failed to load ${activeCfg.label}.`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords();
    activeCfg.fields
      .filter((f) => f.type === "reference")
      .forEach((f) => loadRefTable(f.refKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, includeInactive]);

  useEffect(() => {
    setSearch("");
  }, [activeKey]);

  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;

    return records.filter((row) =>
      activeCfg.fields.some((f) => {
        const val = row[f.name];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [records, search, activeCfg]);

  function refLabel(refKey, id) {
    if (id == null) return "—";
    const cfg = findConfig(refKey);
    const list = refCache[refKey] || [];
    const match = list.find((r) => r.id === id);
    if (!match) return `#${id}`;
    return match[primaryFieldName(cfg)] ?? `#${id}`;
  }

  function openCreateModal() {
    if (!canEdit) return;
    setModalMode("create");
    setEditingId(null);
    setFormValues(emptyFormValues(activeCfg));
    setFormError(null);
    setModalOpen(true);
  }

  function openEditModal(row) {
    if (!canEdit) return;
    setModalMode("edit");
    setEditingId(row.id);
    const values = {};
    activeCfg.fields.forEach((f) => {
      values[f.name] = row[f.name] ?? "";
    });
    if (activeCfg.hasIsActive) values.is_active = row.is_active !== false;
    setFormValues(values);
    setFormError(null);
    setModalOpen(true);
  }

  function closeModal() {
    if (saving) return;
    setModalOpen(false);
    setFormError(null);
  }

  function handleFieldChange(field, value) {
    setFormValues((prev) => ({ ...prev, [field.name]: value }));
  }

  function validate() {
    for (const f of activeCfg.fields) {
      if (f.required && !String(formValues[f.name] ?? "").trim()) {
        return `${f.label} is required.`;
      }
    }
    if (
      activeCfg.key === "financial_year" &&
      formValues.start_date &&
      formValues.end_date &&
      new Date(formValues.end_date) <= new Date(formValues.start_date)
    ) {
      return "End date must be after start date.";
    }
    return null;
  }

  function buildPayload() {
    const payload = {};
    activeCfg.fields.forEach((f) => {
      const raw = formValues[f.name];
      if (f.type === "reference") {
        payload[f.name] = raw === "" ? null : Number(raw);
      } else {
        payload[f.name] = raw === "" ? null : raw;
      }
    });
    if (activeCfg.hasIsActive) payload.is_active = Boolean(formValues.is_active);
    return payload;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      const payload = buildPayload();

      if (modalMode === "create") {
        const created = await createMasterRecord(activeCfg.path, payload);
        setRecords((prev) => [...prev, created]);
      } else {
        const updated = await updateMasterRecord(activeCfg.path, editingId, payload);

        setRecords((prev) => {
          if (!includeInactive && activeCfg.hasIsActive && updated.is_active === false) {
            return prev.filter((r) => r.id !== editingId);
          }

          return prev.map((r) => (r.id === editingId ? updated : r));
        });
      }

      if (refCache[activeCfg.key]) {
        setRefCache((prev) => ({ ...prev, [activeCfg.key]: undefined }));
      }

      setModalOpen(false);
    } catch (err) {
      setFormError(err.message || "Something went wrong saving this record.");
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(row) {
    if (!canDelete) return;

    setDeletingId(row.id);
    try {
      const result = await deleteMasterRecord(activeCfg.path, row.id);
      if (result?.deleted === "hard") {
        setRecords((prev) => prev.filter((r) => r.id !== row.id));
      } else {
        setRecords((prev) =>
          includeInactive
            ? prev.map((r) => (r.id === row.id ? { ...r, is_active: false } : r))
            : prev.filter((r) => r.id !== row.id)
        );
      }
      setPendingDeleteId(null);
    } catch (err) {
      setError(err.message || "Failed to deactivate/delete this record.");
    } finally {
      setDeletingId(null);
    }
  }

  if (!canView) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <div className="admin-main">
          <Header />
          <div className="master-page">
            <div className="master-state master-error">
              <FiLock size={22} />
              <span>You don't have permission to view master data.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Sidebar />

      <div className="admin-main">
        <Header />

        <div className="master-page">
          <div className="master-layout">
            <nav className="master-tablist">
              {MASTER_TABLE_CONFIG.map((cfg) => (
                <button
                  key={cfg.key}
                  type="button"
                  className={`master-tab${cfg.key === activeKey ? " master-tab--active" : ""}`}
                  onClick={() => setActiveKey(cfg.key)}
                >
                  {cfg.label}
                </button>
              ))}
            </nav>

            <div className="master-panel">
              <div className="master-toolbar">
                <div className="master-search">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder={`Search ${activeCfg.label.toLowerCase()}...`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="master-search-clear"
                      onClick={() => setSearch("")}
                      aria-label="Clear search"
                    >
                      <FiX />
                    </button>
                  )}
                </div>

                {activeCfg.hasIsActive && (
                  <button
                    type="button"
                    className={`master-toggle-btn${includeInactive ? " master-toggle-btn--on" : ""}`}
                    onClick={() => setIncludeInactive((v) => !v)}
                  >
                    {includeInactive ? <FiEye /> : <FiEyeOff />}
                    {includeInactive ? "Showing inactive" : "Active only"}
                  </button>
                )}

                {canEdit && (
                  <button type="button" className="master-add-btn" onClick={openCreateModal}>
                    <FiPlus /> Add {activeCfg.label.replace(/s$/, "")}
                  </button>
                )}
              </div>

              {loading && (
                <div className="master-state">
                  <div className="master-spinner" />
                  Loading {activeCfg.label.toLowerCase()}…
                </div>
              )}

              {!loading && error && (
                <div className="master-state master-error">
                  <FiAlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              {!loading && !error && filteredRecords.length === 0 && (
                <div className="master-state master-empty">
                  <FiLayers size={26} className="master-empty-icon" />
                  <p>
                    {search
                      ? `No ${activeCfg.label.toLowerCase()} match your search.`
                      : `No ${activeCfg.label.toLowerCase()} yet.`}
                  </p>
                </div>
              )}

              {!loading && !error && filteredRecords.length > 0 && (
                <div className="master-table-wrap">
                  <table className="master-table">
                    <thead>
                      <tr>
                        {activeCfg.fields.map((f) => (
                          <th key={f.name}>{f.label}</th>
                        ))}
                        {activeCfg.hasIsActive && <th>Status</th>}
                        <th>Updated</th>
                        {canManage && <th className="col-action">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.map((row) => (
                        <tr key={row.id}>
                          {activeCfg.fields.map((f) => (
                            <td key={f.name}>
                              {f.type === "reference"
                                ? refLabel(f.refKey, row[f.name])
                                : f.type === "date"
                                ? row[f.name] || "—"
                                : row[f.name] ?? "—"}
                            </td>
                          ))}

                          {activeCfg.hasIsActive && (
                            <td>
                              <span
                                className={`master-badge master-badge--${
                                  row.is_active === false ? "inactive" : "active"
                                }`}
                              >
                                {row.is_active === false ? "Inactive" : "Active"}
                              </span>
                            </td>
                          )}

                          <td className="master-cell-secondary">
                            {row.updated_at
                              ? dateTimeFormatter.format(new Date(row.updated_at))
                              : row.created_at
                              ? dateTimeFormatter.format(new Date(row.created_at))
                              : "—"}
                          </td>

                          {canManage && (
                            <td className="col-action">
                              <div className="master-action-group">
                                {canEdit && (
                                  <button
                                    type="button"
                                    className="master-icon-btn"
                                    onClick={() => openEditModal(row)}
                                    aria-label="Edit"
                                    title="Edit"
                                  >
                                    <FiEdit2 />
                                  </button>
                                )}

                                {canDelete &&
                                  (pendingDeleteId === row.id ? (
                                    <span className="master-confirm-inline">
                                      <button
                                        type="button"
                                        className="master-icon-btn master-icon-btn--danger"
                                        onClick={() => confirmDelete(row)}
                                        disabled={deletingId === row.id}
                                        aria-label="Confirm delete"
                                        title="Confirm delete"
                                      >
                                        <FiCheck />
                                      </button>
                                      <button
                                        type="button"
                                        className="master-icon-btn"
                                        onClick={() => setPendingDeleteId(null)}
                                        disabled={deletingId === row.id}
                                        aria-label="Cancel delete"
                                        title="Cancel"
                                      >
                                        <FiX />
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="master-icon-btn master-icon-btn--danger"
                                      onClick={() => setPendingDeleteId(row.id)}
                                      aria-label={
                                        activeCfg.hasIsActive ? "Deactivate" : "Delete"
                                      }
                                      title={
                                        activeCfg.hasIsActive ? "Deactivate" : "Delete"
                                      }
                                    >
                                      <FiTrash2 />
                                    </button>
                                  ))}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="master-modal-overlay" onClick={closeModal}>
          <div className="master-modal" onClick={(e) => e.stopPropagation()}>
            <div className="master-modal-header">
              <h3>
                {modalMode === "create" ? "Add" : "Edit"} {activeCfg.label.replace(/s$/, "")}
              </h3>
              <button
                type="button"
                className="master-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="master-modal-body">
              {activeCfg.fields.map((f) => (
                <div className="master-form-field" key={f.name}>
                  <label htmlFor={`field-${f.name}`}>
                    {f.label}
                    {f.required && <span className="master-required">*</span>}
                  </label>

                  {f.type === "reference" ? (
                    <select
                      id={`field-${f.name}`}
                      value={formValues[f.name] ?? ""}
                      onChange={(e) => handleFieldChange(f, e.target.value)}
                    >
                      <option value="">— Select {f.label} —</option>
                      {(refCache[f.refKey] || []).map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt[primaryFieldName(findConfig(f.refKey))]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`field-${f.name}`}
                      type={f.type === "date" ? "date" : "text"}
                      placeholder={f.placeholder || ""}
                      value={formValues[f.name] ?? ""}
                      onChange={(e) => handleFieldChange(f, e.target.value)}
                    />
                  )}
                </div>
              ))}

              {activeCfg.hasIsActive && (
                <div className="master-form-field">
                  <label htmlFor="field-is_active">
                    Status
                  </label>
                  <select
                    id="field-is_active"
                    value={formValues.is_active ? "active" : "inactive"}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        is_active: e.target.value === "active",
                      }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}

              {formError && (
                <div className="master-form-error">
                  <FiAlertCircle size={14} /> {formError}
                </div>
              )}

              <div className="master-modal-footer">
                <button
                  type="button"
                  className="master-btn master-btn--ghost"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="master-btn master-btn--primary" disabled={saving}>
                  {saving ? "Saving…" : modalMode === "create" ? "Create" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}